import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import storageManager from '@/shared/storageManager';
import { useLogin } from '@/hooks/useLogin';
import { useRegistration } from '@/hooks/useRegistration';
import { CmsCtaType } from '@lucky7ventures/bff-types';
import { IntlShape, useIntl } from 'react-intl';
import { OpenedFrom } from '@/lib/gTagManager';
import { useFluidOpen, useFluidOpenWithBonus } from '@/features/cashier/fluid/fluidPaymentsStore';
import { BonusTermsAndConditionsModalPropsExt, ModalProps, MODALS } from '@/features/modal/types';
import { useModal } from '@/features/modal/useModal';

function createAction(
  navigate: (to: string) => void,
  openModal: (modal: MODALS, modalProps?: ModalProps) => void,
  isReturningUser: boolean,
  openLogin: ({ openedFrom }: { openedFrom: OpenedFrom }) => void,
  openRegistration: ({ openedFrom }: { openedFrom: OpenedFrom }) => void,
  openedFrom: OpenedFrom,
  openFluid: () => void,
  openWithPreselectedBonus: (preselectedBonusCode: string) => void,
  ctaType?: CmsCtaType | null,
  ctaLink?: string | null,
  anchorId?: string | null,
  bonusCode?: string | null,
  customTermsAndConditionsDescription?: string | null,
) {
  switch (ctaType) {
    case null:
      return undefined;
    case CmsCtaType.Authentication: {
      if (isReturningUser) return () => openLogin({ openedFrom });
      if (!isReturningUser) return () => openRegistration({ openedFrom });
      return () => openRegistration({ openedFrom });
    }
    case CmsCtaType.Internal:
      return () => navigate(ctaLink ?? '');
    case CmsCtaType.TermsAndConditions:
      return () => openModal(MODALS.BONUS_TERMS_AND_CONDITIONS);
    case CmsCtaType.CustomTermsAndConditions:
      return () =>
        openModal(MODALS.BONUS_TERMS_AND_CONDITIONS, {
          customTermsAndConditionsDescription,
        } as BonusTermsAndConditionsModalPropsExt);

    case CmsCtaType.Games:
      return () => navigate('/games');
    case CmsCtaType.Cashier:
      return () => (bonusCode ? openWithPreselectedBonus(bonusCode) : openFluid());
    case CmsCtaType.Anchor: {
      return () => {
        if (anchorId) {
          const targetElement = document.getElementById(anchorId);
          if (targetElement !== null) {
            targetElement.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            });
          }
        }
      };
    }
    default:
      return () => window.open(ctaLink ?? '', '_blank');
  }
}

function createText(
  ctaType: CmsCtaType | null,
  isReturningUser: boolean,
  intl: IntlShape,
  ctaText?: string | null,
) {
  if (ctaText) return ctaText;
  if (ctaType === CmsCtaType.Games) {
    return intl.formatMessage({ id: 'hero.loggedIn.gamesButton' });
  }
  if (ctaType === CmsCtaType.Cashier) {
    return intl.formatMessage({ id: 'misc.depositNow' });
  }
  if (
    ctaType === CmsCtaType.TermsAndConditions ||
    ctaType === CmsCtaType.CustomTermsAndConditions
  ) {
    return intl.formatMessage({ id: 'bonusCard.termsLink' });
  }
  if (ctaType === CmsCtaType.Authentication) {
    if (isReturningUser) return intl.formatMessage({ id: 'header.login' });
    return intl.formatMessage({ id: 'header.register' });
  }
  return null;
}

const useCreateCmsCtaData = () => {
  const intl = useIntl();
  const navigate = useAbsoluteNavigate();
  const { openLogin } = useLogin();
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const openFluid = useFluidOpen();
  const openWithPreselectedBonus = useFluidOpenWithBonus();
  const { openModal } = useModal();

  // Registration is removed in restricted mode — Authentication CTAs fall back to login (both
  // the label and the action), so no CMS button opens a (now no-op) registration.
  const shouldUseLogin = !!storageManager.getIsReturningUser() || isRegistrationRemoved;

  return {
    createText: (ctaType: CmsCtaType | null, ctaText: string | null) =>
      createText(ctaType, shouldUseLogin, intl, ctaText),
    createAction: (
      openedFrom: OpenedFrom,
      ctaType: CmsCtaType | null,
      ctaLink: string | null,
      ctaAnchorId: string | null,
      bonusCode?: string | null,
      customTermsAndConditionsDescription?: string | null,
    ) =>
      createAction(
        navigate,
        openModal,
        shouldUseLogin,
        openLogin,
        openRegistration,
        openedFrom,
        openFluid,
        openWithPreselectedBonus,
        ctaType,
        ctaLink,
        ctaAnchorId,
        bonusCode,
        customTermsAndConditionsDescription,
      ),
  };
};

export default useCreateCmsCtaData;
